import React, { useState } from "react";
import { format } from "date-fns";
import { MdDelete } from "react-icons/md";
import ActionButton from "./ActionButton";
import styles from './WorkScheduleAdmin.module.css';

export interface PlannedMinutesListEntry {
  startDate: string;
  minutesPerDay: number[];
}

export interface WorkScheduleHistoryProps {
  workSchedules: PlannedMinutesListEntry[];
  employeeUserId?: string;
  employeeName?: string;
  isAdmin?: boolean;
  onDataChange?: () => void;
}

interface AddScheduleFormData {
  startDate: string;
  minutesPerDay: [number, number, number, number, number];
}

const WorkScheduleHistory: React.FC<WorkScheduleHistoryProps> = ({
  workSchedules,
  employeeUserId,
  employeeName,
  isAdmin = false,
  onDataChange,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<AddScheduleFormData>({
    startDate: '',
    minutesPerDay: [480, 480, 480, 480, 480]
  });

  if (!workSchedules || workSchedules.length === 0) {
    return (
      <div className="stats-container">
        <h2>Arbeitsplan-Verlauf</h2>
        <p>Keine Arbeitsplan-Daten verfügbar.</p>
      </div>
    );
  }

  // Sort entries by date (newest first)
  const sortedEntries = [...workSchedules].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
  );

  const weekdayLabels = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag"];
  const weekdays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];

  const formatMinutesToHours = (minutes: number): string => {
    if (minutes === 0) return '0:00';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const handleAddSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/admin/work-schedules/${employeeUserId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate: formData.startDate,
          minutesPerDay: formData.minutesPerDay
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Hinzufügen des Arbeitsplans');
      }

      // Reset form and close
      setFormData({
        startDate: '',
        minutesPerDay: [480, 480, 480, 480, 480]
      });
      setShowAddForm(false);
      if (onDataChange) onDataChange();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSchedule = async (startDate: string) => {
    if (workSchedules.length <= 1) {
      window.alert('Der einzige Arbeitsplan kann nicht gelöscht werden. Mitarbeiter muss mindestens einen Plan haben.');
      return;
    }

    if (!window.confirm(`Möchtest du den Arbeitsplan ab ${format(new Date(startDate), 'dd.MM.yyyy')} wirklich löschen?`)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/admin/work-schedules/${employeeUserId}?startDate=${startDate}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Löschen des Arbeitsplans');
      }

      if (onDataChange) onDataChange();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMinutesChange = (dayIndex: number, value: string) => {
    const minutes = parseInt(value) || 0;
    const newMinutesPerDay = [...formData.minutesPerDay] as [number, number, number, number, number];
    newMinutesPerDay[dayIndex] = minutes;
    setFormData({ ...formData, minutesPerDay: newMinutesPerDay });
  };

  const renderWorkSchedule = (minutes: number[]) => (
    <>
      {minutes.map((min, idx) => (
        <td key={idx}>
          {min > 0 ? `${Math.floor(min / 60)}:${(min % 60).toString().padStart(2, "0")}` : "-"}
        </td>
      ))}
    </>
  );

  return (
    <>
      <div className="section-header">
        <h2>Arbeitsplan-Verlauf</h2>
        {isAdmin && employeeUserId && employeeName && onDataChange && (
          <ActionButton
            onClick={() => setShowAddForm(true)}
            disabled={isLoading}
          >
            + Plan hinzufügen
          </ActionButton>
        )}
      </div>

      {isAdmin && employeeUserId && employeeName && onDataChange && (
        <div>
          {error && (
            <div style={{ color: 'red', marginTop: '10px' }}>
              {error}
            </div>
          )}

          {showAddForm && (
            <div className={styles.addScheduleFormOverlay}>
              <div className={styles.addScheduleForm}>
                <h4>Neuen Arbeitsplan für {employeeName} hinzufügen</h4>
                <form onSubmit={handleAddSchedule}>
                  <div className={styles.formGroup}>
                    <label htmlFor="startDate">Startdatum:</label>
                    <input
                      type="date"
                      id="startDate"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Minuten pro Tag:</label>
                    <div className={styles.minutesPerDayGrid}>
                      {weekdays.map((day, index) => (
                        <div key={day} className={styles.dayInput}>
                          <label>{day}:</label>
                          <input
                            type="number"
                            min="0"
                            max="720"
                            value={formData.minutesPerDay[index]}
                            onChange={(e) => handleMinutesChange(index, e.target.value)}
                            required
                          />
                          <span className={styles.hoursDisplay}>
                            ({formatMinutesToHours(formData.minutesPerDay[index])})
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.formActions}>
                    <ActionButton type="submit" disabled={isLoading}>
                      {isLoading ? 'Wird hinzugefügt...' : 'Plan hinzufügen'}
                    </ActionButton>
                    <ActionButton
                      type="button"
                      variant="secondary"
                      onClick={() => setShowAddForm(false)}
                      disabled={isLoading}
                    >
                      Abbrechen
                    </ActionButton>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="table-wrapper">
        <table className="history-table work-schedule">
            <thead>
              <tr>
                <th>Gültig ab</th>
                {weekdayLabels.map((label, index) => (
                  <th key={index}>{label}</th>
                ))}
                {isAdmin && <th className="actions">Aktionen</th>}
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map((entry, index) => {
                try {
                  return (
                    <tr key={index}>
                      <td>{format(new Date(entry.startDate), "dd.MM.yyyy")}</td>
                      {renderWorkSchedule(entry.minutesPerDay)}
                      {isAdmin && (
                        <td className="actions">
                          <button
                            onClick={() => handleDeleteSchedule(entry.startDate)}
                            className="delete-icon-button"
                            disabled={isLoading || workSchedules.length <= 1}
                            title={workSchedules.length <= 1 ? "Der einzige Plan kann nicht gelöscht werden" : "Diesen Plan löschen"}
                          >
                            {MdDelete({})}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                } catch (error) {
                  console.error(`Error rendering work schedule entry at index ${index}:`, entry, error);
                  return null; // Skip problematic entries
                }
              })}
            </tbody>
          </table>
      </div>
    </>
  );
};

export default WorkScheduleHistory;